import React from "react";
import { useTranslation } from "react-i18next";

import { Can } from "@/casl.config";
import { normalizePrice } from "@helpers/utils";
import { calculateLineMargin, formatMarginPercentage } from "../contributionMargin";
import { toOrderCurrencyCost } from "../orderCurrency";
import { useOrderCurrency } from "./OrderCurrencyContext";

/**
 * Compact "LPP 45 · 43.8%" line rendered under an order line's unit price, in both the order editor
 * and the saved order view. The LPP is taken in the base currency and shown in the order currency.
 *
 * Gating lives inside, like in ContributionMarginDetails: these figures expose cost prices, and the
 * editor has no other permission checks to fall back on.
 */
export function LineMarginCaption({
    unitPrice,
    discount,
    lastPurchasePrice,
}: {
    unitPrice: number;
    discount?: number | null;
    lastPurchasePrice?: number | null;
}) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders.lpp" });

    const orderCurrency = useOrderCurrency();

    const margin = calculateLineMargin({
        unitPrice,
        discount,
        lastPurchasePrice: toOrderCurrencyCost(lastPurchasePrice, orderCurrency),
    });

    return (
        <Can I="price.view" a="sale_order">
            <div className="text-xs font-thin text-gray-600 whitespace-nowrap">
                {t("cell", {
                    lpp: normalizePrice(margin.lastPurchasePrice),
                    margin: formatMarginPercentage(margin.marginPercentage),
                })}
            </div>
        </Can>
    );
}

/**
 * Explains the "LPP" abbreviation used by the line captions. Rendered as a footnote under the
 * order summary, away from the figures themselves.
 */
export function LastPurchasePriceLegend() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders.lpp" });

    return (
        <Can I="price.view" a="sale_order">
            <div className="mt-[24px] text-sm font-thin text-gray-600">{t("legend")}</div>
        </Can>
    );
}
